import React, { useEffect, useState } from "react";
import { Loader2, X, Search } from "lucide-react";
import useProject from "../../../hooks/useProject";
import useSettings from "../../../hooks/useSettings";
import settingsService from "../../../services/settingsService";

const GrantAccessModal = ({ project, onClose }) => {
  const { allUsers, isFetchingUsers, loadAllUsers, clearUsers } = useProject();
  const { loadProjectsWithAccess } = useSettings();
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [isGranting, setIsGranting] = useState(false);

  useEffect(() => {
    loadAllUsers(true);
    return () => clearUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const memberIds = new Set(
    (project?.members || []).map((m) => String(m._id || m.id)),
  );
  const availableUsers = allUsers.filter(
    (u) => !memberIds.has(String(u._id || u.id)),
  );
  const filtered = availableUsers.filter((u) =>
    (u.name || u.email || "").toLowerCase().includes(search.toLowerCase()),
  );

  const toggle = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    setError("");
  };

  const handleGrant = async () => {
    if (selectedUserIds.length === 0) {
      setError("Please select at least one resource.");
      return;
    }
    // One request for the whole selection. Granting one user at a time via
    // the project "Add Member" route rejected client accounts and could stop
    // halfway, leaving some selected users granted and others not.
    setIsGranting(true);
    setError("");
    try {
      await settingsService.grantProjectAccess(project._id, selectedUserIds);
      loadProjectsWithAccess();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to grant access.",
      );
    } finally {
      setIsGranting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] max-h-[90vh] overflow-y-auto -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Grant Access</h2>
            <p className="text-sm text-gray-400 mt-0.5">
              Add a new member and assign them to a project
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <hr className="my-4" />

        {/* Project Name */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-800 mb-1 block">
            Project Name
          </label>
          <div className="w-full h-10 bg-gray-50 border border-gray-200 rounded-lg px-3 flex items-center text-sm text-gray-700">
            {project?.name}
          </div>
        </div>

        {/* Project Members */}
        <div className="mb-4">
          <label className="text-sm font-semibold text-gray-800 mb-2 block">
            Project Members
          </label>
          {isFetchingUsers ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-[#4253C7]" size={24} />
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              {/* Search */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                <Search size={14} className="text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search Members"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 text-sm outline-none bg-transparent placeholder:text-gray-400"
                />
              </div>
              {/* List — fixed height regardless of result count, so the
                  card doesn't grow/shrink as the search filters members */}
              <div className="h-[220px] overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">
                    No members available
                  </p>
                ) : (
                  filtered.map((user) => {
                    const id = user._id || user.id;
                    const selected = selectedUserIds.includes(id);
                    return (
                      <div
                        key={id}
                        onClick={() => toggle(id)}
                        className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-none"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-400 flex-shrink-0">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.5"
                            >
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                              <circle cx="12" cy="7" r="4" />
                            </svg>
                          </div>
                          <span className="text-sm text-gray-600">
                            {user.name || user.email} -{" "}
                            {user.role
                              ? user.role.charAt(0).toUpperCase() +
                                user.role.slice(1)
                              : "Member"}
                          </span>
                        </div>
                        {/* Checkbox */}
                        <div
                          className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${selected ? "bg-[#4253C7] border-[#4253C7]" : "border-gray-300"}`}
                        >
                          {selected && (
                            <svg
                              width="11"
                              height="11"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-2 bg-[#F0F2FF] rounded-xl px-4 py-3 mb-4">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4253C7"
            strokeWidth="2"
            className="flex-shrink-0 mt-0.5"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <p className="text-xs text-[#4253C7] leading-relaxed">
            The selected resource will immediately gain access to this project
            and appear in the project list
          </p>
        </div>

        {error && <p className="text-sm text-red-500 mb-3">{error}</p>}

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleGrant}
            disabled={isGranting || isFetchingUsers}
            className="flex-1 h-11 rounded-xl bg-[#4253C7] text-white text-sm font-semibold hover:bg-[#3644b0] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {isGranting && <Loader2 size={14} className="animate-spin" />}
            Grant Access
          </button>
        </div>
      </div>
    </>
  );
};

export default GrantAccessModal;
